'use client';

import type BigNumber from 'bignumber.js';
import { type Atom } from 'jotai';
import { createContext, useContext } from 'react';

import {
  type BaseDefiYieldOverviewResponse,
  type GetDefiYieldDetailsResponse,
  type Strategy,
} from '@flipster/apis/v1/dynamic-earn/types';

export interface FaqItem {
  title: React.ReactNode;
  description: React.ReactNode;
}

export interface AdvantageItem {
  icon: React.ReactNode;
  title: React.ReactNode;
  desc: React.ReactNode;
}

export interface ProductDetailsConfig {
  // Atoms
  detailsAtom: Atom<GetDefiYieldDetailsResponse | undefined>;
  sortedStrategiesAtom: Atom<Strategy[]>;
  hasSubscribedBalanceAtom: Atom<boolean>;
  overviewAtom: Atom<BaseDefiYieldOverviewResponse | undefined>;
  collateralRateAtom: Atom<BigNumber>;

  // Product metadata
  productName: string;
  currency: string;
  pricePrecision: number;
  subscribeDeepLinkPath: string;

  // Optional features
  strategyProviderPath?: string;
  strategyProviderLink?: React.ReactNode;
  learnMorePath?: string;

  // Functions
  openSubscribeModal: () => void;

  // Content
  advantageItems?: AdvantageItem[];
  getFaqItems: (details: GetDefiYieldDetailsResponse | undefined) => FaqItem[];
}

const ProductConfigContext = createContext<ProductDetailsConfig | null>(null);

export const ProductConfigProvider = ({
  value,
  children,
}: {
  value: ProductDetailsConfig;
  children: React.ReactNode;
}) => {
  return <ProductConfigContext.Provider value={value}>{children}</ProductConfigContext.Provider>;
};

export const useProductConfig = () => {
  const config = useContext(ProductConfigContext);
  if (!config) {
    throw new Error('useProductConfig must be used within a ProductConfigProvider');
  }
  return config;
};
